import React, { useEffect, useState, useCallback } from "react";
import {
  Typography,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Checkbox,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { saveAs } from "file-saver";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const GenerateFee = () => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
    const [isMaster, setIsMaster] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(1);
  // Dropdown data
  const [ddlClass, setDdlClass] = useState([]);
  const [ddlSection, setDdlSection] = useState([]);
  const [ddlStudent, setDdlStudent] = useState([]);
  const [ddlClassType, setDdlClassType] = useState([]);
  const [ddlSession, setDdlSession] = useState([]);
  const [ddlMonth, setDdlMonth] = useState([]);

  // Filter state
  const [sessionIds, setSessionIds] = useState([]);
  const [monthIds, setMonthIds] = useState([]);
  const [selectedClassType, setSelectedClassType] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [paidStatus, setPaidStatus] = useState(-1);

  // Pagination
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);

  // Table & loading
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExcelLoading, setIsExcelLoading] = useState(false);

  // ── Fetch helpers ──────────────────────────────────────────────────────────

  const fetchDdlClassType = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/CreateClass/ddlClassType`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`Error: ${res.status}`);
      setDdlClassType(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchBranches = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const data = await response.json();
        setBranches(data);
      } else {
        console.error("Failed to fetch branches");
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const fetchDdlClass = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/Enquiry/ddlClassName`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`Error: ${res.status}`);
      const data = await res.json();
      if (data.data === null && data.msg === "Record Not Found") return;
      setDdlClass(data);
    } catch (e) { console.error(e); }
  };

  const fetchDdlSection = async (classId) => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/Exam/ddlSection_clsId`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ classId, teacherId: 0 }),
      });
      if (!res.ok) throw new Error(`Error: ${res.status}`);
      const data = await res.json();
      setDdlSection(data?.data === null ? [] : data);
    } catch (e) { console.error(e); }
  };

  const fetchDdlStudent = async (classId, sectionId) => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/FeeReport/ddlStudentbyClassId`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ classId, sectionId }),
      });
      if (!res.ok) throw new Error(`Error: ${res.status}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setDdlStudent(data);
      } else {
        setDdlStudent([]);
        setSnackbarMessage(data.msg || "Unexpected response format!");
        setOpenSnackbar(true);
      }
    } catch (e) {
      setSnackbarMessage("An error occurred while fetching students!");
      setOpenSnackbar(true);
    }
  };

const fetchDdlSession = async () => {
  try {
    const token = sessionStorage.getItem("token");
    const res = await fetch(`${process.env.REACT_APP_BASE_URL}/ClassPromotion/GetFinancialYear`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: token },
      body: JSON.stringify({}),
    });
    if (!res.ok) throw new Error(`Error: ${res.status}`);
    const data = await res.json();
    setDdlSession(data);

    // Auto-select the last (most recent) session
    if (data.length > 0) {
      setSessionIds([data[data.length - 1].financialYearID]);
    }
  } catch (e) { console.error(e); }
};

  const fetchDdlMonth = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/Fine/GetMonthList`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`Error: ${res.status}`);
      const data = await res.json();
      setDdlMonth(data);

      // Auto-select the current month
      const currentMonthId = new Date().getMonth() + 1; // JS months are 0-indexed
      const match = data.find((m) => m.monthId === currentMonthId);
      if (match) {
        setMonthIds([match.monthId]);
      }
    } catch (e) { console.error(e); }
  };

useEffect(() => {
    fetchDdlClass();
    fetchDdlClassType();
    fetchDdlSession();
    fetchDdlMonth();
  }, []);

  useEffect(() => {
    const isMasterUser = sessionStorage.getItem("is_Master") === "true";
    setIsMaster(isMasterUser);

    if (isMasterUser) {
      fetchBranches();
    } else {
      const branchId = sessionStorage.getItem("branchId");
      setSelectedBranchId(branchId);
    }
  }, []);

  useEffect(() => {
    if (selectedClass !== "") {
      fetchDdlSection(selectedClass);
      setSelectedSection("");
      setSelectedStudent("");
      setDdlStudent([]);
    } else {
      setDdlSection([]);
      setSelectedSection("");
      setSelectedStudent("");
      setDdlStudent([]);
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedSection !== "") {
      fetchDdlStudent(selectedClass, selectedSection);
      setSelectedStudent("");
    } else {
      setDdlStudent([]);
      setSelectedStudent("");
    }
  }, [selectedSection]);

  const buildRequestBody = useCallback(
    (page, size) => ({
      classId: selectedClass ? parseInt(selectedClass) : 0,
      sectionId: selectedSection ? parseInt(selectedSection) : 0,
      studentId: selectedStudent ? parseInt(selectedStudent) : 0,
      classTypeId: selectedClassType ? parseInt(selectedClassType) : 0,
      paidStatus,
      SessionIds: sessionIds.length ? sessionIds.join(",") : null,
      MonthIds: monthIds.length ? monthIds.join(",") : null,
      pageNumber: page,
      pageSize: size,
    }),
    [selectedClass, selectedSection, selectedStudent, selectedClassType, paidStatus, sessionIds, monthIds]
  );

 const generateReport = useCallback(
    async (page, size) => {
      setIsLoading(true);
      try {
        const token = sessionStorage.getItem("token");
        const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
        const res = await fetch(`${process.env.REACT_APP_BASE_URL}/FeeReport/ReportFeeGenerateNew?Branch_Id=${branchId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify(buildRequestBody(page, size)),
        });
        if (!res.ok) {
          setSnackbarMessage("Error: Please provide proper input!");
          setOpenSnackbar(true);
          return;
        }
        const data = await res.json();
        if (data.msg === "Record Not Found") {
          setSnackbarMessage("No records found!");
          setOpenSnackbar(true);
          setReportData([]);
          setTotalRecords(0);
          return;
        }
        setReportData(data.data);
        setTotalRecords(data.totalRecords);
      } catch (e) {
        setSnackbarMessage("An error occurred while fetching report data!");
        setOpenSnackbar(true);
      } finally {
        setIsLoading(false);
      }
    },
    [buildRequestBody, isMaster, selectedBranchId]
  );


  // This useeffect auto hit the report generation Api. 
  // useEffect(() => {
  //   setPageNumber(1);
  //   generateReport(1, pageSize);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [selectedClass, selectedSection, selectedStudent, selectedClassType, paidStatus, sessionIds, monthIds]);

  useEffect(() => {
    generateReport(pageNumber, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber]);

  const handlePageSizeChange = (e) => {
    const s = parseInt(e.target.value);
    setPageSize(s);
    setPageNumber(1);
    generateReport(1, s);
  };

  const handlePageChange = (e, value) => setPageNumber(value);

  const generateExcel = async () => {
    setIsExcelLoading(true);
    try {
      const token = sessionStorage.getItem("token");
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/FeeReport/ReportFeeGenerateExcel?Branch_Id=${branchId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify(buildRequestBody(pageNumber, pageSize)),
      });
      if (!res.ok) {
        setSnackbarMessage("Error: Failed to download Excel!");
        setOpenSnackbar(true);
        return;
      }
      saveAs(await res.blob(), `fee_report_${new Date().toISOString().split("T")[0]}.xlsx`);
    } catch (e) {
      setSnackbarMessage("An error occurred while downloading Excel!");
      setOpenSnackbar(true);
    } finally {
      setIsExcelLoading(false);
    }
  };

  // ── Styles ─────────────────────────────────────────────────────────────────

  // Same blue border/hover as original, just slightly cleaner radius & shadow
  const selectStyles = {
    width: "100%",
    borderRadius: "10px",
    background: "#ffffff",
    boxShadow: "0 2px 6px rgba(0,0,0,0.07)",
    transition: "box-shadow 0.2s ease",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2196F3",
      borderWidth: "1.5px",
    },
    "&:hover": {
      boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#1976D2" },
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#21CBF3",
      borderWidth: "2px",
    },
    "&.Mui-disabled": { opacity: 0.55 },
    "& .MuiSelect-select": {
      padding: "11px 16px",
      fontWeight: 500,
      color: "#333",
      fontSize: "0.875rem",
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        borderRadius: "10px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
        mt: 0.5,
        "& .MuiMenuItem-root": {
          fontSize: "0.875rem",
          "&:hover": { backgroundColor: "#e3f2fd" },
          "&.Mui-selected": {
            backgroundColor: "#e3f2fd",
            "&:hover": { backgroundColor: "#bbdefb" },
          },
        },
      },
    },
  };

  const fieldLabelSx = {
    display: "block",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#546e7a",
    mb: 0.6,
    letterSpacing: "0.03em",
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ padding: "24px", minHeight: "100vh" }}>

       

      {/* ── Filter card ── */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "14px",
          border: "1px solid #e0e0e0",
          p: "22px 24px 20px",
          mb: 3,
          background: "#ffffff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        {/* Grid: responsive 3-col → 2-col → 1-col */}
      <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: "18px 24px",
            mb: 2.5,
          }}
        >
          {/* Branch — Master users only */}
          {isMaster && (
            <Box>
              <Typography component="label" sx={fieldLabelSx}>Branch</Typography>
              <Select
                fullWidth
                value={selectedBranchId || ""}
                onChange={(e) => setSelectedBranchId(e.target.value)}
                displayEmpty
                sx={selectStyles}
                MenuProps={menuProps}
              >
                <MenuItem value="" disabled><em>Select Branch</em></MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.branchId} value={branch.branchId}>
                    {branch.branchName}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}

          {/* Session */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>Academic Session</Typography>
           <Select
  multiple
  fullWidth
  value={sessionIds}
  // Session Select onChange
onChange={(e) => {
  const value = e.target.value;
  if (value.includes("select_all")) {
    setSessionIds(
      sessionIds.length === ddlSession.length
        ? []
        : ddlSession.map((s) => s.financialYearID)
    );
  } else {
    setSessionIds(value);
  }
}}
  displayEmpty
  sx={selectStyles}
  renderValue={(selected) =>
    selected.length === 0
      ? <em style={{ color: "#aaa", fontStyle: "italic", fontWeight: 400 }}>Select Sessions</em>
      : ddlSession
          .filter((s) => selected.includes(s.financialYearID))
          .map((s) => s.finanacialYear)
          .join(", ")
  }
  MenuProps={menuProps}
>
  <MenuItem value="" disabled>
  <em>Select Sessions</em>
</MenuItem>

{/* ADD THIS */}
// Session "Select All" MenuItem — remove onClick, add value
<MenuItem value="select_all">
  <Checkbox
    checked={sessionIds.length === ddlSession.length && ddlSession.length > 0}
    indeterminate={sessionIds.length > 0 && sessionIds.length < ddlSession.length}
    sx={{ color: "#2196F3", "&.Mui-checked": { color: "#2196F3" } }}
  />
  <ListItemText primary="Select All" />
</MenuItem>
 {[...ddlSession].reverse().map((s) => (
    <MenuItem key={s.financialYearID} value={s.financialYearID}>
      <Checkbox
        checked={sessionIds.indexOf(s.financialYearID) > -1}
        sx={{ color: "#2196F3", "&.Mui-checked": { color: "#2196F3" } }}
      />
      <ListItemText primary={s.finanacialYear} />
    </MenuItem>
  ))}
</Select>
          </Box>

          {/* Month */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>Month</Typography>
          <Select
  multiple
  fullWidth
  value={monthIds}
 // Month Select onChange
onChange={(e) => {
  const value = e.target.value;
  if (value.includes("select_all")) {
    setMonthIds(
      monthIds.length === ddlMonth.length
        ? []
        : ddlMonth.map((m) => m.monthId)
    );
  } else {
    setMonthIds(value);
  }
}}
  displayEmpty
  sx={selectStyles}
  renderValue={(selected) =>
    selected.length === 0
      ? <em style={{ color: "#aaa", fontStyle: "italic", fontWeight: 400 }}>Select Months</em>
      : ddlMonth
          .filter((m) => selected.includes(m.monthId))
          .map((m) => m.month)
          .join(", ")
  }
  MenuProps={menuProps}
>
  <MenuItem value="" disabled>
  <em>Select Months</em>
</MenuItem>

{/* ADD THIS */}

<MenuItem value="select_all">
  <Checkbox
    checked={monthIds.length === ddlMonth.length && ddlMonth.length > 0}
    indeterminate={monthIds.length > 0 && monthIds.length < ddlMonth.length}
    sx={{ color: "#2196F3", "&.Mui-checked": { color: "#2196F3" } }}
  />
  <ListItemText primary="Select All" />
</MenuItem>
  {ddlMonth.map((m) => (
    <MenuItem key={m.monthId} value={m.monthId}>
      <Checkbox
        checked={monthIds.indexOf(m.monthId) > -1}
        sx={{ color: "#2196F3", "&.Mui-checked": { color: "#2196F3" } }}
      />
      <ListItemText primary={m.month} />
    </MenuItem>
  ))}
</Select>
          </Box>

          {/* Class Type */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>Class Type</Typography>
            <Select
              fullWidth
              value={selectedClassType}
              onChange={(e) => setSelectedClassType(e.target.value)}
              displayEmpty
              sx={selectStyles}
              MenuProps={menuProps}
            >
              <MenuItem value=""><em>All Class Types</em></MenuItem>
              {ddlClassType.map((t) => (
                <MenuItem key={t.classTypeId} value={t.classTypeId}>{t.classType}</MenuItem>
              ))}
            </Select>
          </Box>

          {/* Class */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>Class</Typography>
            <Select
              fullWidth
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              displayEmpty
              sx={selectStyles}
              MenuProps={menuProps}
            >
              <MenuItem value=""><em>All Classes</em></MenuItem>
              {ddlClass.map((c) => (
                <MenuItem key={c.classId} value={c.classId}>{c.className}</MenuItem>
              ))}
            </Select>
          </Box>

          {/* Section */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>Section</Typography>
            <Tooltip title={!selectedClass ? "Select a class first" : ""} placement="top">
              <Select
                fullWidth
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                displayEmpty
                disabled={!selectedClass}
                sx={selectStyles}
                MenuProps={menuProps}
              >
                <MenuItem value=""><em>All Sections</em></MenuItem>
                {ddlSection.map((s) => (
                  <MenuItem key={s.sectionId} value={s.sectionId}>{s.sectionName}</MenuItem>
                ))}
              </Select>
            </Tooltip>
          </Box>

          {/* Student */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>Student</Typography>
            <Tooltip title={!selectedSection ? "Select a section first" : ""} placement="top">
              <Select
                fullWidth
                value={selectedStudent}
                onChange={(e) => setSelectedStudent(e.target.value)}
                displayEmpty
                disabled={!selectedSection}
                sx={selectStyles}
                MenuProps={menuProps}
              >
                <MenuItem value=""><em>All Students</em></MenuItem>
                {ddlStudent.map((s) => (
                  <MenuItem key={s.studentId} value={s.studentId}>{s.studentName}</MenuItem>
                ))}
              </Select>
            </Tooltip>
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ borderTop: "1px solid #f0f0f0", pt: 2 }}>

          {/* Paid Status — original RadioGroup, unchanged */}
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <Typography sx={{ ...fieldLabelSx, mb: 0.75 }}>Payment Status</Typography>
            <RadioGroup
              value={paidStatus}
              onChange={(e) => setPaidStatus(parseInt(e.target.value))}
              row
            >
              <FormControlLabel
                value={-1}
                control={<Radio sx={{ color: "#2196F3", "&.Mui-checked": { color: "#2196F3" } }} />}
                label="All"
              />
              <FormControlLabel
                value={0}
                control={<Radio sx={{ color: "#2196F3", "&.Mui-checked": { color: "#2196F3" } }} />}
                label="Unpaid"
              />
              <FormControlLabel
                value={1}
                control={<Radio sx={{ color: "#2196F3", "&.Mui-checked": { color: "#2196F3" } }} />}
                label="Paid"
              />
            </RadioGroup>
          </FormControl>

          {/* Actions row */}
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>

            {/* Page size */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: "0.82rem", color: "#666" }}>Rows per page:</Typography>
              <Select
                value={pageSize}
                onChange={handlePageSizeChange}
                size="small"
                sx={{
                  width: "80px",
                  borderRadius: "8px",
                  fontSize: "0.82rem",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#2196F3" },
                  "& .MuiSelect-select": { py: "7px" },
                }}
              >
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={200}>200</MenuItem>
              </Select>
            </Box>

            {/* Generate — original blue gradient */}
            <Button
              variant="contained"
              onClick={() => generateReport(pageNumber, pageSize)}
              disabled={isLoading}
              sx={{
                background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                borderRadius: "25px",
                padding: "9px 28px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                minWidth: "160px",
                boxShadow: "0 3px 5px 2px rgba(33,203,243,.3)",
                transition: "all 0.25s ease",
                "&:hover": {
                  background: "linear-gradient(45deg, #1976D2 30%, #1E88E5 90%)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 5px 10px rgba(33,203,243,.4)",
                },
              }}
            >
              {isLoading ? <CircularProgress size={20} color="inherit" /> : "Generate Report"}
            </Button>

            {/* Excel — original red gradient */}
            <Button
              variant="contained"
              onClick={generateExcel}
              disabled={isExcelLoading}
              sx={{
                background: "linear-gradient(45deg, #f44336 30%, #ef5350 90%)",
                borderRadius: "25px",
                padding: "9px 28px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                minWidth: "170px",
                boxShadow: "0 3px 5px 2px rgba(244,67,54,.3)",
                transition: "all 0.25s ease",
                "&:hover": {
                  background: "linear-gradient(45deg, #d32f2f 30%, #e53935 90%)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 5px 10px rgba(244,67,54,.4)",
                },
              }}
            >
              {isExcelLoading
                ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Preparing…</>
                : "Download Excel"
              }
            </Button>

            {/* Record count */}
            {totalRecords > 0 && (
              <Typography sx={{ fontSize: "0.82rem", color: "#888", ml: "auto" }}>
                Showing{" "}
                <strong style={{ color: "#333" }}>{Math.min(pageSize, reportData.length)}</strong>
                {" "}of{" "}
                <strong style={{ color: "#333" }}>{totalRecords}</strong>
                {" "}records
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>

      {/* ── Table area ── */}
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#2196F3" }} />
        </Box>
      ) : reportData.length > 0 ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "14px",
            border: "1px solid #e0e0e0",
            overflow: "hidden",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
        >
          <TableContainer
           sx={{
    maxHeight: 500, // Set your desired fixed height
    overflow: "auto", // Enable vertical scrolling
  }}>
            <Table size="small">
              <TableHead
               sx={{
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "#e3f2fd", // Match your header background
  }}>
                <TableRow sx={{ backgroundColor: "#e3f2fd" }}>
                  {[
                    "Student Name", "Adm. No", "Roll No", "Class", "Section",
                    "Fee Generated Date", "Month", "Session",
                    "Generated Amount", "Concession", "Fine", "Discount",
                    "Paid Amount", "Balance", "Total Fee", "Paid Status",
                    "Total Charges", "Charge Breakup",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      align={["Generated Amount","Concession","Fine","Discount","Paid Amount","Balance","Total Fee","Total Charges"].includes(h) ? "right" : "left"}
                      sx={{
                        fontSize: "0.74rem",
                        fontWeight: 700,
                        color: "#1565c0",
                        whiteSpace: "nowrap",
                        py: 1.5,
                        borderBottom: "2px solid #bbdefb",
                        letterSpacing: "0.02em",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((item, idx) => (
                  <TableRow
                    key={item.feePaymentID}
                    hover
                    sx={{
                      backgroundColor: idx % 2 === 0 ? "#ffffff" : "#fafafa",
                      "&:hover": { backgroundColor: "#e3f2fd" },
                      "& td": {
                        fontSize: "0.82rem",
                        color: "#333",
                        borderBottom: "1px solid #f0f0f0",
                        py: 1.1,
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{item.studentName}</TableCell>
                    <TableCell>{item.admissionNo}</TableCell>
                    <TableCell>{item.rollNo || "—"}</TableCell>
                    <TableCell>{item.className}</TableCell>
                    <TableCell>{item.sectionName}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{item.feeGeneratedDate}</TableCell>
                    <TableCell>{item.monthName}</TableCell>
                    <TableCell>{item.session}</TableCell>
                    <TableCell align="right">₹{item.generatedAmount.toFixed(2)}</TableCell>
                    <TableCell align="right">₹{item.concession.toFixed(2)}</TableCell>
                    <TableCell align="right">₹{item.fine.toFixed(2)}</TableCell>
                    <TableCell align="right">₹{item.discountAmount.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>₹{item.paidAmount.toFixed(2)}</TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 600, color: item.balance > 0 ? "#d32f2f" : "#388e3c" }}
                    >
                      ₹{item.balance.toFixed(2)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>₹{item.totalFee.toFixed(2)}</TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          backgroundColor: item.paidStatus === 1 ? "#4caf50" : "#f44336",
                          color: "#fff",
                          padding: "3px 10px",
                          borderRadius: "12px",
                          display: "inline-block",
                          fontWeight: 600,
                          fontSize: "0.74rem",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.paidStatusText}
                      </Box>
                    </TableCell>
                    <TableCell align="right">₹{item.totalChargeAmount.toFixed(2)}</TableCell>
                    <TableCell>
                      <Tooltip title={item.chargeBreakup} arrow placement="left">
                        <span style={{ cursor: "help", borderBottom: "1px dashed #bbb" }}>
                          {item.chargeBreakup?.length > 25
                            ? `${item.chargeBreakup.substring(0, 25)}…`
                            : item.chargeBreakup}
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: "flex", justifyContent: "center", py: 2, borderTop: "1px solid #f0f0f0" }}>
            <Pagination
              count={Math.ceil(totalRecords / pageSize)}
              page={pageNumber}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        </Paper>
      ) : (
        <Box sx={{ textAlign: "center", py: 10 }}>
          <Typography sx={{ fontSize: "2rem", mb: 1 }}>📋</Typography>
          <Typography variant="h6" sx={{ color: "#555", fontWeight: 600 }}>No Records Found</Typography>
          <Typography sx={{ fontSize: "0.875rem", color: "#999", mt: 0.5 }}>
            Try adjusting your filters to see results
          </Typography>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="error" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GenerateFee;